<template>
  <div
    :class="$style.customAttributeContainer"
  >
    <MembershipSuggestionRules
      :handleReviewSuggestions="handleReviewSuggestions"
      :isAutomationEnabled="isAutomationEnabled"
      parentType="user_group"
    />
    <MembershipSuggestionSettings
      v-if="$hasFeatureFlag($featureFlags.membershipSuggestionAutomation)"
      parentType="user_group"
      @isAutomationEnabled="handleAutomationEnabled"
    />
    <h3
      :class="[$style.header, 'customHeader']"
    >
      {{ getHeaderText }}
    </h3>
    <SystemUserItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :inDetailsAsidePanel="false"
      panelType="groupAsidePanel"
      :watchSelectedItems="isAutomationEnabled"
      @selectedItemsChanged="handleSelectedItemsChanged"
    />
  </div>
</template>

<script>
import {
  ItemTypeConsts,
  StoreModelMap, StoreModuleMap,
} from 'client/util/Constants';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  xor,
} from 'lodash';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import MembershipSuggestionRules from 'client/groups/User/MembershipSuggestionRules.vue';
import MembershipSuggestionSettings from 'client/groups/User/MembershipSuggestionSettings.vue';
import SystemUserItemTable from 'client/systemusers/SystemUserItemTable/SystemUserItemTable.vue';

const systemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];

export default {
  name: 'UserGroupSystemUsers',

  components: {
    MembershipSuggestionRules,
    MembershipSuggestionSettings,
    SystemUserItemTable,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  props: {
    boundItemsUrl: {
      default: '',
      required: false,
      type: String,
    },
    handleBoundUsersChanged: {
      default: () => {},
      type: Function,
    },
    handleReviewSuggestions: {
      default: () => {},
      type: Function,
    },
  },

  data() {
    return {
      isAutomationEnabled: false,
    };
  },

  computed: {
    getExemptionsListMapped() {
      const { memberQueryExemptions } = this.item;
      return memberQueryExemptions.map((user) => user.id);
    },
    getHeaderText() {
      if (this.isManagedByActiveDirectory) {
        if (this.isItemBeingCreated) {
          return 'The following users are managed by AD Integration';
        }
        return `${this.displayName} manages the following users by AD Integration: `;
      }

      if (this.isItemBeingCreated) {
        return 'Select users to add to this user group';
      }

      return `${this.displayName} has the following members: `;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
    const userGroupModel = StoreModelMap[this.$options.propsData.parentType];

    this.$options.computed = {
      ...this.$options.computed,
      ...mapGetters(userGroupModel, [
        'isItemBeingCreated',
        'isManagedByActiveDirectory',
      ]),
      ...mapState(userGroupModel, { displayName: state => state.item.name }),
    };
  },

  methods: {
    handleAutomationEnabled(value) {
      this.isAutomationEnabled = value;
    },
    async handleSelectedItemsChanged(ids, action) {
      const users = {
        action,
        ids,
        names: '',
      };
      const { areUsersOnExemptionList, noExemptionList } = this.validateExemptionList(ids);
      const showWarning = (!areUsersOnExemptionList
      && this.isAutomationEnabled && noExemptionList.length);

      if (noExemptionList && noExemptionList.length) {
        const { results } = await this.fetchUsersById({ ids: noExemptionList });
        const usersMapped = results.map((user) => (`${user.username}`));
        users.names = usersMapped.join(', ');
      }

      this.handleBoundUsersChanged(!!showWarning, users);
    },

    validateExemptionList(items) {
      const exemptionList = [];
      let areUsersOnExemptionList = true;
      items.forEach((item) => {
        if (this.getExemptionsListMapped.includes(item)) {
          exemptionList.push(item);
        }
      });

      const noExemptionList = xor(items, exemptionList);

      if (noExemptionList.length) areUsersOnExemptionList = false;

      return { areUsersOnExemptionList, exemptionList, noExemptionList };
    },

    ...mapActions(systemUserCollection, [
      'fetchUsersById',
    ]),
  },
};
</script>

<style module>
@value (jcGrey600) from '~css/brand-colors.css';

.containsAlerts {
  position: relative;
}

.customAttributeContainer {
  height: 100%;
  overflow: scroll;
  padding: 10px 20px 10px;
  width: 100%;
}

.header {
  color: jcGrey600 !important;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
  margin: 0 !important;
  padding: 20px 20px 3px !important;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
